// The Monaco loader of the Scripts IDE (spec/dashboard.md section 10.8): Monaco is bundled locally and loaded lazily (its
// own chunks); the workers are the editor worker and the TypeScript / JavaScript worker (no JSON / CSS / HTML); the
// JavaScript defaults carry the editor typings (`huelency-editor.d.ts`) and, per branch, the model types (`models.d.ts`).
// Nothing here runs at import time: vitest + jsdom cannot run Monaco, and the tests stub this module.
import { EDITOR_TYPINGS } from './typings';

type Monaco = typeof import('monaco-editor');

let loading: Promise<Monaco> | null = null;
let modelsLib: { content: string; dispose: () => void } | null = null;

export const EDITOR_TYPINGS_PATH = 'file:///huelency-editor.d.ts';
export const MODEL_TYPINGS_PATH = 'file:///models.d.ts';

/** Loads Monaco once (the next call answers the same promise); a failed load may be retried. */
export function loadMonaco(): Promise<Monaco> {
  if (!loading) {
    loading = (async () => {
      self.MonacoEnvironment = {
        getWorker(_workerId: string, label: string) {
          if (label === 'typescript' || label === 'javascript') {
            return new Worker(new URL('monaco-editor/esm/vs/language/typescript/ts.worker.js', import.meta.url), { type: 'module' });
          }
          return new Worker(new URL('monaco-editor/esm/vs/editor/editor.worker.js', import.meta.url), { type: 'module' });
        },
      };
      // the package has no `main` (only `module`): the entry file by path keeps vitest's resolver (and the tests' stubs) happy
      const monaco = (await import('monaco-editor/esm/vs/editor/editor.main.js')) as Monaco;
      const js = monaco.languages.typescript.javascriptDefaults;
      js.setCompilerOptions({
        allowNonTsExtensions: true,
        allowJs: true,
        checkJs: false,
        // ES2022 (9 in the TypeScript enum; monaco's ScriptTarget enum stops at ES2020 / ESNext)
        target: 9 as import('monaco-editor').languages.typescript.ScriptTarget,
        lib: ['es2022'],
      });
      js.addExtraLib(EDITOR_TYPINGS, EDITOR_TYPINGS_PATH);
      return monaco;
    })();
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
}

/** The branch's generated model types as `models.d.ts`; replaced when the schema (the catalog) changes. */
export function setModelTypings(monaco: Monaco, content: string): void {
  if (modelsLib?.content === content) return;
  modelsLib?.dispose();
  const lib = monaco.languages.typescript.javascriptDefaults.addExtraLib(content, MODEL_TYPINGS_PATH);
  modelsLib = { content, dispose: () => lib.dispose() };
}
